import { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Search,
  Megaphone,
  ExternalLink,
  Hash,
  Plus,
  Copy,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { FlagIcon } from '@/components/FlagIcon';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { FordShareModal } from '@/components/FordShareModal';
import { updateCampaign, type Campaign, type FordShareEntry } from '@/api/campaigns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { FieldIssue } from '../types';

const FORDSHARE_URL_TEMPLATE =
  'https://ford.brandshare.net/global/advert/ActivityDetails.aspx?activityId=';

interface CampaignDetailsProps {
  campaign: Campaign | null;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  showWarnings?: boolean;
  editableFields?: string[];
}

function Row({
  label,
  index,
  tooltip,
  tooltipClass,
  children,
}: {
  label: string;
  index: number;
  tooltip: string;
  tooltipClass?: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07, ease: [0.4, 0, 0.2, 1] }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase mb-0.5 w-fit cursor-default">
            {label}
          </p>
        </TooltipTrigger>
        <TooltipContent className={tooltipClass}>{tooltip}</TooltipContent>
      </Tooltip>
      {children}
    </motion.div>
  );
}

function Text({ value }: { value: string | null | undefined }) {
  return (
    <p className="text-sm text-slate-700 font-medium inline-block transition-transform duration-150 hover:scale-[1.06] origin-left">
      {value || <span className="text-slate-300">—</span>}
    </p>
  );
}

export function CampaignDetails({
  campaign,
  fieldIssues,
  showErrors = true,
  showWarnings = true,
  editableFields = [],
}: CampaignDetailsProps) {
  const [showFordShareModal, setShowFordShareModal] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { fordshare_entries: FordShareEntry[] | null }) =>
      updateCampaign(campaign!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
    onError: () => {
      toast.error('Failed to update FordShare IDs');
    },
  });

  const handleFordShareSave = (entries: FordShareEntry[]) => {
    const newEntries = entries.length > 0 ? entries : [];

    // Optimistic update so the UI shows the new IDs immediately
    queryClient.setQueryData<{ campaigns: Campaign[] }>(['campaigns', false], (old) => {
      if (!old) return old;
      return {
        ...old,
        campaigns: old.campaigns.map((c) =>
          c.id === campaign!.id ? { ...c, fordshare_entries: newEntries } : c,
        ),
      };
    });

    mutation.mutate({ fordshare_entries: newEntries });
  };

  if (!campaign) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 flex flex-col flex-shrink-0">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Campaign Details
          </p>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-x-6 gap-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <div className="h-2.5 w-14 bg-slate-100 rounded-full mb-1.5 animate-pulse" />
              <div className="h-4 w-24 bg-slate-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isSearch = campaign.channel === 'Paid Search';
  const entries = campaign.fordshare_entries ?? [];
  const canEditFordShare = editableFields.includes('fordshare_entries');

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col flex-shrink-0">
      <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Campaign Details
        </p>
        {campaign.channel && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wider bg-slate-100 text-slate-500">
            {isSearch ? <Search className="w-3 h-3" /> : <Megaphone className="w-3 h-3" />}
            {isSearch ? 'Search Ad' : 'Social Ad'}
          </span>
        )}
      </div>
      <div className="px-5 py-4 grid grid-cols-2 gap-x-6 gap-y-3">
        <Row label="Platform" index={0} tooltip="The social media platform this campaign runs on">
          <div className="inline-block transition-transform duration-150 hover:scale-[1.06] origin-left">
            <PlatformIcon platform={campaign.platform} />
          </div>
        </Row>
        <Row label="Market" index={1} tooltip="The geographic market this campaign targets">
          {campaign.market ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-sm bg-slate-100 text-slate-700 rounded-lg transition-transform duration-150 hover:scale-[1.06] origin-left">
              <FlagIcon countryCode={campaign.market} className="w-4 h-auto rounded-sm" />
              {campaign.market}
            </span>
          ) : (
            <Text value={null} />
          )}
        </Row>
        <Row label="Quarter" index={2} tooltip="The fiscal quarter this campaign is planned for">
          <Text value={campaign.quarter} />
        </Row>
        <Row label="Nameplate" index={3} tooltip="The Ford vehicle model this campaign promotes">
          <Text value={campaign.nameplate} />
        </Row>
        <Row
          label="Business Unit"
          index={4}
          tooltip="The Ford division responsible for this campaign"
        >
          <Text value={campaign.business_unit} />
        </Row>
        <Row
          label="Buy Type"
          index={5}
          tooltip="How the media placement is purchased (e.g. programmatic, direct)"
        >
          <Text value={campaign.buy_type} />
        </Row>
        <Row
          label="Format"
          index={6}
          tooltip="The creative format specification for this placement"
        >
          <Text value={campaign.format} />
        </Row>
        <Row label="Size" index={7} tooltip="The format size or aspect ratio for this placement">
          <Text value={campaign.size} />
        </Row>
        <Row
          label="Tier"
          index={8}
          tooltip="The campaign tier level (e.g. national, regional, local)"
        >
          <Text value={campaign.tier} />
        </Row>
        <Row label="Language" index={9} tooltip="The language used for this campaign's creative">
          <Text value={campaign.language} />
        </Row>

        <div className="group/fs">
          <Row
            label="FordShare IDs"
            index={10}
            tooltip="FordShare Activity IDs linked to this campaign"
          >
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              {entries.map((entry, i) => {
                const isLast = i === entries.length - 1;
                const badge = (
                  <HoverCard key={entry.id}>
                    <HoverCardTrigger asChild>
                      <button
                        onClick={() => canEditFordShare && setShowFordShareModal(true)}
                        className={`px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5 hover:bg-slate-200 transition-colors ${canEditFordShare ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <Hash className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-medium">{entry.id}</span>
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-auto min-w-[200px]" side="bottom" align="start">
                      <div className="space-y-2.5">
                        <div className="space-y-0.5">
                          <p className="text-sm text-slate-800 flex items-center gap-1.5 group/hcid">
                            <span className="text-slate-400 font-normal">ID:</span>
                            <span className="font-semibold">{entry.id}</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(entry.id);
                                toast.success('FordShare ID copied');
                              }}
                              className="p-0.5 rounded text-slate-300 hover:text-slate-500 transition-colors opacity-0 group-hover/hcid:opacity-100"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </p>
                          {entry.delivery_date && (
                            <p className="text-xs text-slate-400">
                              Delivery date:{' '}
                              {new Date(entry.delivery_date + 'T00:00:00').toLocaleDateString(
                                'en-GB',
                                { day: 'numeric', month: 'short', year: 'numeric' },
                              )}
                            </p>
                          )}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={`${FORDSHARE_URL_TEMPLATE}${entry.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#0014cc] rounded-lg hover:bg-[#0010a3] transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Open in FordShare
                            </a>
                          </TooltipTrigger>
                          <TooltipContent
                            className="text-[10px]"
                            style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}
                          >
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${FORDSHARE_URL_TEMPLATE}${entry.id}`,
                                  );
                                  toast.success('URL copied');
                                }}
                                className="p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <span>
                                {FORDSHARE_URL_TEMPLATE}
                                {entry.id}
                              </span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );

                if (isLast) {
                  return (
                    <div key={entry.id} className="flex items-center gap-1.5">
                      {badge}
                      {canEditFordShare && (
                        <button
                          onClick={() => setShowFordShareModal(true)}
                          className="w-7 h-7 rounded-lg inline-flex items-center justify-center border-2 border-dashed border-slate-400 text-slate-400 hover:border-slate-600 hover:text-slate-600 transition-all opacity-0 group-hover/fs:opacity-100"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                }
                return badge;
              })}
              {entries.length === 0 && canEditFordShare && (
                <button
                  onClick={() => setShowFordShareModal(true)}
                  className="w-7 h-7 rounded-lg inline-flex items-center justify-center border-2 border-dashed border-slate-400 text-slate-400 hover:border-slate-600 hover:text-slate-600 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {fieldIssues?.['fordshare_entries'] &&
              (() => {
                const fi = fieldIssues['fordshare_entries'];
                const msgs: { type: 'error' | 'warning'; text: string }[] = [
                  ...(showErrors
                    ? fi.errors.map((m) => ({ type: 'error' as const, text: m }))
                    : []),
                  ...(showWarnings
                    ? fi.warnings.map((m) => ({ type: 'warning' as const, text: m }))
                    : []),
                ];
                if (msgs.length === 0) return null;
                return (
                  <div className="mt-1 space-y-0.5">
                    {msgs.map((m, i) => (
                      <p
                        key={i}
                        className={`text-xs flex items-start gap-1.5 ${m.type === 'error' ? 'text-red-600' : 'text-amber-600'}`}
                      >
                        {m.type === 'error' ? (
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                        )}
                        {m.text}
                      </p>
                    ))}
                  </div>
                );
              })()}
          </Row>
        </div>
      </div>

      <FordShareModal
        isOpen={showFordShareModal}
        onClose={() => setShowFordShareModal(false)}
        entries={entries}
        onSave={handleFordShareSave}
        isLoading={mutation.isPending}
      />
    </div>
  );
}
